import { useState } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { esAdministrativo, type Pagina } from '@kodica/shared';

import {
  Campo,
  Cargando,
  Celda,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Fila,
  Paginador,
  Panel,
  Tabla,
  Vacio,
} from '../componentes/ui';
import { consulta, useRecurso } from '../lib/consultas';
import { useSesion } from '../lib/sesion';

/**
 * Poder contestar qué pasó.
 *
 * Kodica registraba cada entrada, cada orden de abrir una pluma —con el acuse
 * del hardware— y cada acción administrativa desde el primer día, y **nada de
 * eso se podía consultar**: la bitácora existía para sostener una aclaración con
 * el condominio y no había forma de abrirla.
 *
 * Las pestañas van **en la dirección** (`/historial/aperturas`) y no en estado
 * de React: esta es la pantalla que alguien abre para responderle a un residente
 * enojado, y tiene que poder mandarle el enlace a su jefe.
 *
 * Cada pestaña responde a una pregunta que alguien hace de verdad:
 *  · «Un residente reclama que alguien entró a su casa el martes.»
 *  · «La pluma no abrió y el guardia dice que sí le picó.»
 *  · «¿Quién dio de baja este tag?»
 *
 * Hubo una quinta, «¿le llegó el aviso al residente?», y se quitó. Listaba
 * las notificaciones de entrada, no los avisos que escribe la administración,
 * y con ese nombre en la nube era una tabla vacía permanente: los accesos
 * ocurren en la caseta. Quien busca «los avisos que mandé» va a **Avisos**,
 * que es donde están.
 */

type Pestana = 'ingresos' | 'aperturas' | 'movimientos';

const PESTANAS: { id: Pestana; texto: string; soloAdmin?: boolean }[] = [
  { id: 'ingresos', texto: 'Ingresos' },
  { id: 'aperturas', texto: 'Aperturas de pluma' },
  { id: 'movimientos', texto: 'Movimientos del sistema', soloAdmin: true },
];

const ORIGEN: Record<string, string> = {
  CASETA: 'Caseta',
  KIOSCO_QR: 'Código QR',
  TAG_UHF: 'Tag',
  FACIAL: 'Rostro',
  MANUAL: 'Manual',
};

function fecha(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function HistorialPagina() {
  const { pestana = 'ingresos' } = useParams<{ pestana: Pestana }>();
  const { usuario } = useSesion();
  const admin = usuario ? esAdministrativo(usuario.rol) : false;

  const [pagina, setPagina] = useState(1);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [soloFallidas, setSoloFallidas] = useState(false);

  const visibles = PESTANAS.filter((p) => admin || !p.soloAdmin);
  const actual = visibles.find((p) => p.id === pestana);

  // Una pestaña que este rol no puede ver no da un error críptico del servidor:
  // se cae a la primera, que sí puede.
  const ruta = consulta(`/historial/${pestana}`, {
    pagina,
    porPagina: 25,
    desde: desde ? new Date(`${desde}T00:00:00`).toISOString() : undefined,
    hasta: hasta ? new Date(`${hasta}T23:59:59`).toISOString() : undefined,
    soloFallidas: soloFallidas || undefined,
  });
  const { datos, cargando, error } = useRecurso<Pagina<Record<string, never>>>(
    actual ? ruta : null,
  );

  if (!actual) return <Navigate to="/historial/ingresos" replace />;

  const filas = (datos?.datos ?? []) as unknown[];
  // Solo donde significa algo: en los movimientos del sistema no hay «fallido».
  const admiteFallidas = pestana === 'aperturas';

  return (
    <>
      <Encabezado titulo="Historial" descripcion="Qué pasó, cuándo y quién lo hizo" />

      <div className="mb-stack-md flex flex-wrap gap-2">
        {visibles.map((p) => (
          <NavLink
            key={p.id}
            to={`/historial/${p.id}`}
            onClick={() => setPagina(1)}
            className={({ isActive }) =>
              `toque inline-flex items-center rounded-lg px-4 py-2 text-label-bold transition-all ${
                isActive
                  ? 'bg-secondary-container text-on-secondary-container'
                  : 'border border-outline-variant bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high'
              }`
            }
          >
            {p.texto}
          </NavLink>
        ))}
      </div>

      <div className="mb-stack-md grid gap-3 sm:grid-cols-3">
        <Campo etiqueta="Desde">
          <Entrada
            type="date"
            value={desde}
            onChange={(e) => {
              setPagina(1);
              setDesde(e.target.value);
            }}
          />
        </Campo>
        <Campo etiqueta="Hasta">
          <Entrada
            type="date"
            value={hasta}
            onChange={(e) => {
              setPagina(1);
              setHasta(e.target.value);
            }}
          />
        </Campo>
        {admiteFallidas && (
          <Campo etiqueta="Filtrar">
            <label className="toque mt-1 flex items-center gap-2 text-body-md text-on-surface">
              <input
                type="checkbox"
                checked={soloFallidas}
                onChange={(e) => {
                  setPagina(1);
                  setSoloFallidas(e.target.checked);
                }}
                className="h-5 w-5"
              />
              Solo lo que falló
            </label>
          </Campo>
        )}
      </div>

      <Panel>
        {error && <ErrorCaja>{error}</ErrorCaja>}
        {cargando && <Cargando />}
        {!cargando && !error && datos?.total === 0 && (
          <Vacio>No hay nada registrado en ese periodo.</Vacio>
        )}

        {!cargando && !error && datos && datos.total > 0 && (
          <>
            {pestana === 'ingresos' && <TablaIngresos filas={filas as Ingreso[]} />}
            {pestana === 'aperturas' && <TablaAperturas filas={filas as Apertura[]} />}
            {pestana === 'movimientos' && <TablaMovimientos filas={filas as Movimiento[]} />}
            <Paginador
              pagina={datos.pagina}
              paginas={datos.paginas}
              total={datos.total}
              alCambiar={setPagina}
            />
          </>
        )}
      </Panel>
    </>
  );
}

// ── Ingresos ──────────────────────────────────────────────────

interface Ingreso {
  id: string;
  nombreVisitante: string | null;
  entradaEn: string;
  salidaEn: string | null;
  minutosEstancia: number | null;
  /** Solo en los pasos de residente: hacia dónde iba. */
  sentido?: 'ENTRADA' | 'SALIDA' | null;
  estado: string;
  origen: string;
  unidad: { clave: string } | null;
  puntoAcceso: { nombre: string } | null;
  guardia: { nombre: string; apellidoPaterno: string | null } | null;
}

function TablaIngresos({ filas }: { filas: Ingreso[] }) {
  return (
    <Tabla
      cabeceras={['Entrada', 'Visitante', 'Vivienda', 'Cómo entró', 'Acceso', 'Autorizó', 'Estado']}
    >
      {filas.map((a) => (
        <Fila key={a.id}>
          <Celda>{fecha(a.entradaEn)}</Celda>
          <Celda fuerte>{a.nombreVisitante || 'Sin nombre'}</Celda>
          <Celda fuerte>{a.unidad?.clave ?? '—'}</Celda>
          <Celda>{ORIGEN[a.origen] ?? a.origen.toLowerCase()}</Celda>
          <Celda>{a.puntoAcceso?.nombre ?? '—'}</Celda>
          <Celda>
            {a.guardia ? `${a.guardia.nombre} ${a.guardia.apellidoPaterno ?? ''}`.trim() : '—'}
          </Celda>
          <Celda>
            <Chip tono={tonoEstado(a.estado)}>{textoEstado(a)}</Chip>
          </Celda>
        </Fila>
      ))}
    </Tabla>
  );
}

// ── Aperturas de pluma ────────────────────────────────────────

interface Apertura {
  id: string;
  ocurridoEn: string;
  origen: string;
  exitosa: boolean;
  acusadaEn: string | null;
  error: string | null;
  pasoConfirmado: boolean;
  carril: { nombre: string; puntoAcceso: { nombre: string } } | null;
  ordenadaPor: { nombre: string; apellidoPaterno: string | null } | null;
  acceso: { nombreVisitante: string | null; unidad: { clave: string } | null } | null;
}

function TablaAperturas({ filas }: { filas: Apertura[] }) {
  return (
    <Tabla cabeceras={['Cuándo', 'Carril', 'Quién la ordenó', 'Por qué', 'Resultado']}>
      {filas.map((a) => (
        <Fila key={a.id}>
          <Celda>{fecha(a.ocurridoEn)}</Celda>
          <Celda fuerte>
            {a.carril ? `${a.carril.puntoAcceso.nombre} · ${a.carril.nombre}` : '—'}
          </Celda>
          <Celda>
            {a.ordenadaPor
              ? `${a.ordenadaPor.nombre} ${a.ordenadaPor.apellidoPaterno ?? ''}`.trim()
              : (ORIGEN[a.origen] ?? 'Automática')}
          </Celda>
          <Celda>
            {a.acceso?.unidad?.clave ?? a.acceso?.nombreVisitante ?? ORIGEN[a.origen] ?? '—'}
          </Celda>
          <Celda>
            {/* La distinción que resuelve la discusión: «no le picó» frente a
                «le picó y la pluma no respondió». Sin el acuse del hardware es
                la palabra de uno contra la del otro. */}
            {a.exitosa ? (
              <Chip tono="ok">Abrió{a.pasoConfirmado ? ' y pasó' : ''}</Chip>
            ) : a.acusadaEn ? (
              <Chip tono="malo">{a.error ?? 'La pluma falló'}</Chip>
            ) : (
              <Chip tono="aviso">Sin respuesta del aparato</Chip>
            )}
          </Celda>
        </Fila>
      ))}
    </Tabla>
  );
}

// ── Movimientos del sistema ───────────────────────────────────

interface Movimiento {
  id: string;
  accion: string;
  entidad: string;
  ocurridoEn: string;
  usuario: { nombre: string; apellidoPaterno: string | null; rol: string } | null;
}

function TablaMovimientos({ filas }: { filas: Movimiento[] }) {
  return (
    <Tabla cabeceras={['Cuándo', 'Quién', 'Qué hizo', 'Sobre qué']}>
      {filas.map((m) => (
        <Fila key={m.id}>
          <Celda>{fecha(m.ocurridoEn)}</Celda>
          <Celda fuerte>
            {m.usuario
              ? `${m.usuario.nombre} ${m.usuario.apellidoPaterno ?? ''}`.trim()
              : 'Sistema'}
          </Celda>
          <Celda>{m.accion.replace(/\./g, ' · ')}</Celda>
          <Celda>{m.entidad}</Celda>
        </Fila>
      ))}
    </Tabla>
  );
}

/**
 * Cómo se pinta el estado de un ingreso.
 *
 * `REGISTRADO` es el paso de un residente, y no es ni bueno ni malo: es un
 * hecho anotado. Va en gris para que la vista no invite a leerlo como algo que
 * atender —no hay nada que hacer con él— y dice el sentido, que es lo único que
 * aporta: si iba entrando o saliendo.
 */
function tonoEstado(estado: string): 'ok' | 'malo' | 'neutro' {
  if (estado === 'DENTRO') return 'ok';
  if (estado === 'RECHAZADO') return 'malo';
  return 'neutro';
}

function textoEstado(a: {
  estado: string;
  sentido?: 'ENTRADA' | 'SALIDA' | null;
  minutosEstancia: number | null;
}): string {
  switch (a.estado) {
    case 'DENTRO':
      return 'Dentro';
    case 'SALIO':
      return `Salió${a.minutosEstancia ? ` · ${a.minutosEstancia} min` : ''}`;
    case 'REGISTRADO':
      return a.sentido === 'SALIDA' ? 'Pasó · salida' : 'Pasó · entrada';
    default:
      return a.estado.toLowerCase();
  }
}
